/**
 * ThemeProvider — Injects CSS custom properties from theme configuration.
 *
 * Resolves themeColors (base 11 tokens) + themeConfig (extended tokens + design controls)
 * into CSS variables on a wrapper div. All widgets/components reference these variables
 * for consistent theming on Drops, Hubs, and any future host surface.
 *
 * Design controls (borderRadius, cardStyle, shadowIntensity, spacing) map to CSS variables
 * that consumers read. This avoids runtime style computation — everything is
 * CSS-variable-driven.
 *
 * Font loading: Automatically injects Google Fonts <link> tags for any non-system font
 * families configured in typography. Cleans up on unmount / font change.
 *
 * NOTE: CSS variables retain the `--hub-*` prefix from the original HubThemeProvider lift.
 * A separate cleanup pass will generalize to a neutral prefix (e.g. `--dp-*`) across the
 * widget library at once — bulk rename across SCSS + consumers is safer as one atomic pass
 * than file-by-file during this lift batch.
 */

import { useMemo, useEffect, CSSProperties } from "react";
import { SurfaceThemeColors, SurfaceTypography, SurfaceThemeConfigResponse } from "@/hooks/hub/useGetHub";

/* Per-tenant theme overrides — applied as a BASE layer that Drop/Hub themeColors can still override on a per-instance basis. Kick MVP needs platform-recognizable colors when the host surface is Kick rather than DevourPlay native. */
export type TenantId = "devourplay" | "kick";

const TENANT_THEMES: Record<TenantId, Partial<SurfaceThemeColors>> = {
    devourplay: {
        /* Native — no overrides; falls through to themeColors entirely */
    },
    kick: {
        primary: "#53FC18",
        primaryDark: "#3FCB12",
        background: "#0E0E10",
        cardBackground: "#16161B",
        textPrimary: "#FFFFFF",
        textSecondary: "#A0A0A8",
    },
};

interface ThemeProviderProps {
    themeColors?: SurfaceThemeColors;
    typography?: SurfaceTypography;
    themeConfig?: SurfaceThemeConfigResponse;
    /*
     * Host tenant — when set, tenant-specific brand tokens are applied as a
     * base layer. Drop/Hub themeColors STILL override individual fields per
     * the merge in cssVars below, so a creator's own brand colors survive
     * inside a tenant shell. Default DevourPlay = no overrides.
     */
    tenantId?: TenantId;
    children: React.ReactNode;
}

const BORDER_RADIUS_MAP: Record<string, string> = {
    none: "0px",
    sm: "4px",
    md: "8px",
    lg: "12px",
    pill: "999px",
};

const SHADOW_MAP: Record<string, string> = {
    none: "none",
    subtle: "0 2px 8px rgba(0,0,0,0.15)",
    medium: "0 4px 16px rgba(0,0,0,0.25)",
    dramatic: "0 8px 32px rgba(0,0,0,0.4)",
};

const SPACING_MAP: Record<string, string> = {
    compact: "1.5rem",
    comfortable: "2.5rem",
    generous: "4rem",
};

const BODY_SIZE_MAP: Record<string, string> = {
    sm: "14px",
    md: "16px",
    lg: "18px",
};

/** Only allow https:// URLs for background images — prevents CSS injection via url() breakout */
const SAFE_URL_RE = /^https:\/\//;

/**
 * System/generic font families that don't need loading.
 * Lowercase for comparison.
 */
const SYSTEM_FONTS = new Set([
    "inherit", "sans-serif", "serif", "monospace", "cursive", "fantasy",
    "system-ui", "-apple-system", "blinkmacsystemfont", "segoe ui",
    "roboto", "helvetica neue", "arial", "noto sans",
]);

/**
 * Injects Google Fonts <link> tags for custom font families.
 * Only loads fonts that aren't system/generic fonts.
 * Each font gets weight range 300-900 for flexibility.
 */
function useGoogleFontLoader(typography?: SurfaceTypography) {
    const fontFamilies = useMemo(() => {
        if (!typography) return [];
        const families = new Set<string>();
        const slots = [typography.headingFont, typography.bodyFont, typography.accentFont, typography.displayFont];
        for (const font of slots) {
            if (font && !SYSTEM_FONTS.has(font.toLowerCase().trim())) {
                families.add(font.trim());
            }
        }
        return Array.from(families);
    }, [typography]);

    useEffect(() => {
        if (fontFamilies.length === 0) return;

        const links: HTMLLinkElement[] = [];

        for (const family of fontFamilies) {
            // Skip if already loaded by another ThemeProvider instance
            const existingId = `font-${family.replace(/\s+/g, "-").toLowerCase()}`;
            if (document.getElementById(existingId)) continue;

            const link = document.createElement("link");
            link.id = existingId;
            link.rel = "stylesheet";
            link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family)}:wght@300;400;500;600;700;800;900&display=swap`;
            document.head.appendChild(link);
            links.push(link);
        }

        return () => {
            for (const link of links) {
                link.remove();
            }
        };
    }, [fontFamilies]);
}

/*
 * A SURFACE'S THEME IS ITS TOKENS, NOT `html[data-theme]`.
 *
 * This component briefly set and cleared `data-theme` from the surface's
 * preset. That was wrong twice over: the attribute is global and shared with
 * the whole app, so clearing it for a light Drop left every other page on the
 * light branch of ~1100 `@include dark-mode` rules for the rest of the
 * session; and a Drop hosted inside PageWrapper would have dragged the
 * platform navbar, footer and every modal light with it.
 *
 * The preset already expresses light-vs-dark where it belongs — in the
 * `--hub-*` tokens below. The element resets stay on the app's own setting,
 * which every entry pins to dark.
 */
export const ThemeProvider = ({
    themeColors,
    typography,
    themeConfig,
    tenantId,
    children,
}: ThemeProviderProps) => {
    // Load Google Fonts for custom typography
    useGoogleFontLoader(typography);

    /*
     * Merge tenant base layer with Drop/Hub themeColors. Per-instance themeColors
     * win on a per-field basis — a creator's pinned accent color survives even
     * when hosted on a Kick-tenant surface.
     */
    const mergedColors: SurfaceThemeColors | undefined = useMemo(() => {
        const tenantOverrides = tenantId ? TENANT_THEMES[tenantId] : undefined;
        if (!tenantOverrides && !themeColors) return undefined;
        return { ...tenantOverrides, ...themeColors } as SurfaceThemeColors;
    }, [tenantId, themeColors]);

    const cssVars = useMemo(() => {
        const vars: Record<string, string> = {};

        // Base color tokens (tenant base merged with Drop/Hub overrides)
        if (mergedColors?.primary) vars["--hub-primary"] = mergedColors.primary;
        if (mergedColors?.primaryDark) vars["--hub-primaryDark"] = mergedColors.primaryDark;
        if (mergedColors?.secondary) vars["--hub-secondary"] = mergedColors.secondary;
        if (mergedColors?.secondaryDark) vars["--hub-secondaryDark"] = mergedColors.secondaryDark;
        if (mergedColors?.accent) vars["--hub-accent"] = mergedColors.accent;
        if (mergedColors?.background) vars["--hub-background"] = mergedColors.background;
        if (mergedColors?.cardBackground) vars["--hub-cardBackground"] = mergedColors.cardBackground;
        if (mergedColors?.textPrimary) vars["--hub-textPrimary"] = mergedColors.textPrimary;
        if (mergedColors?.textSecondary) vars["--hub-textSecondary"] = mergedColors.textSecondary;
        if (mergedColors?.success) vars["--hub-success"] = mergedColors.success;
        if (mergedColors?.error) vars["--hub-error"] = mergedColors.error;

        // Extended color tokens (from themeConfig)
        if (themeConfig?.surfaceGlow) vars["--hub-surfaceGlow"] = themeConfig.surfaceGlow;
        if (themeConfig?.borderSubtle) vars["--hub-borderSubtle"] = themeConfig.borderSubtle;
        if (themeConfig?.gradientStart) vars["--hub-gradientStart"] = themeConfig.gradientStart;
        if (themeConfig?.gradientEnd) vars["--hub-gradientEnd"] = themeConfig.gradientEnd;
        if (themeConfig?.navBackground) vars["--hub-navBackground"] = themeConfig.navBackground;
        if (themeConfig?.heroOverlay) vars["--hub-heroOverlay"] = themeConfig.heroOverlay;

        // Design controls → CSS variables
        const radius = themeConfig?.borderRadius || "md";
        vars["--hub-radius"] = BORDER_RADIUS_MAP[radius] || "8px";

        const shadow = themeConfig?.shadowIntensity || "subtle";
        vars["--hub-shadow"] = SHADOW_MAP[shadow] || SHADOW_MAP.subtle;

        const gap = themeConfig?.spacing || "comfortable";
        vars["--hub-section-gap"] = SPACING_MAP[gap] || SPACING_MAP.comfortable;

        const bodyFontSize = themeConfig?.bodySize || "md";
        vars["--hub-body-size"] = BODY_SIZE_MAP[bodyFontSize] || "16px";

        // Card style
        const style = themeConfig?.cardStyle || "elevated";
        if (style === "glass") {
            vars["--hub-card-backdrop"] = "blur(12px)";
            vars["--hub-card-bg-opacity"] = "0.6";
        } else if (style === "bordered") {
            vars["--hub-card-border"] = `1px solid ${themeConfig?.borderSubtle || "rgba(255,255,255,0.1)"}`;
        } else if (style === "elevated") {
            vars["--hub-card-shadow"] = vars["--hub-shadow"];
        }
        // flat = no additional card styling

        // Typography
        if (typography?.headingFont) vars["--hub-heading-font"] = typography.headingFont;
        if (typography?.bodyFont) vars["--hub-body-font"] = typography.bodyFont;
        if (typography?.accentFont) vars["--hub-accent-font"] = typography.accentFont;
        if (typography?.displayFont) vars["--hub-display-font"] = typography.displayFont;

        // Extended typography
        if (themeConfig?.headingWeight) vars["--hub-heading-weight"] = String(themeConfig.headingWeight);
        if (themeConfig?.headingLineHeight) vars["--hub-heading-lh"] = String(themeConfig.headingLineHeight);
        if (themeConfig?.headingLetterSpacing !== undefined) {
            // headingLetterSpacing stored as integer (-2 to 5). Convert directly to px for visible effect.
            vars["--hub-heading-ls"] = `${themeConfig.headingLetterSpacing}px`;
        }
        if (themeConfig?.headingTransform && themeConfig.headingTransform !== "none") {
            vars["--hub-heading-transform"] = themeConfig.headingTransform;
        }

        return vars;
    }, [mergedColors, typography, themeConfig]);

    const style: CSSProperties = {
        ...(cssVars as CSSProperties),
        backgroundColor: mergedColors?.background,
        color: mergedColors?.textPrimary,
        fontSize: cssVars["--hub-body-size"],
        // Page-level background image (full-bleed behind all content blocks)
        ...themeConfig?.backgroundImageUrl && SAFE_URL_RE.test(themeConfig.backgroundImageUrl.trim()) ? {
            backgroundImage: `url(${themeConfig.backgroundImageUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundAttachment: "fixed",
            backgroundRepeat: "no-repeat",
        } : {},
    };

    return (
        <div className="theme-root" style={style}>
            {children}
        </div>
    );
};
